import {useCallback, useRef, useState, useEffect} from 'react';

// Comment icon as data URI to avoid network fetch delay on text selection toolbar
// fromsrc/assets/new-comment.svg
const CommentIcon = `data:image/svg+xml,${encodeURIComponent('<svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.125 11.0208C20.1283 12.2857 19.8328 13.5335 19.2625 14.6625C18.5863 16.0154 17.5468 17.1534 16.2605 17.9489C14.9741 18.7444 13.4916 19.1661 11.9792 19.1667C10.7143 19.17 9.46653 18.8744 8.3375 18.3042L2.875 20.125L4.69583 14.6625C4.12556 13.5335 3.83004 12.2857 3.83333 11.0208C3.83392 9.50835 4.25559 8.0259 5.05111 6.73952C5.84663 5.45315 6.98458 4.41367 8.3375 3.7375C9.46653 3.16722 10.7143 2.8717 11.9792 2.875H12.4583C14.4558 2.9852 16.3425 3.82831 17.7571 5.2429C19.1717 6.6575 20.0148 8.54416 20.125 10.5417V11.0208Z" stroke="#87D065" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 8V14" stroke="#87D065" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 11H15" stroke="#87D065" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>')}`;

// Delay before checking if a markup(highlight) annotation has comments
// This gives time for the comment to be created after the annotation
const ORPHAN_CHECK_DELAY_MS = 300;

// Check if document has editing restrictions
// permissions: https://www.nutrient.io/api/web/enums/NutrientViewer.DocumentPermissions.html
const checkDocumentRestrictions = async (instance) => {
  try {
    const permissions = await instance.getDocumentPermissions();
    return permissions.annotationsAndForms === false;
  } catch (err) {
    console.warn('[useNutrientViewer] Failed to check permissions:', err);
    return false;
  }
};

export const useNutrientViewer = ({
  containerRef,
  documentId,
  serverUrl,
  authToken,
  creatorName,
  mentionableUsers = [],
  onReady,
  onError,
}) => {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDocumentRestricted, setIsDocumentRestricted] = useState(false);

  // Nutrient instance refs
  const instanceRef = useRef(null);
  const NutrientRef = useRef(null);
  const isInitializingRef = useRef(false);
  const isUnloadedRef = useRef(false);
  const viewerContainerRef = useRef(null);

  const footingAnnotationIdsRef = useRef(new Set());
  const footingDataMapRef = useRef(new Map()); // Map<footingId, { annotationId, pageIndex }>
  const activeFootingIdRef = useRef(null);
  const pendingMarkupCheckTimersRef = useRef(new Map()); // Map<annotationId, timeoutId>
  const zoomLevelElementRef = useRef(null);

  const isTextSelectionMarkup = useCallback((annotation) => {
    const NutrientViewer = NutrientRef.current;
    if (!NutrientViewer) return false;

    // Check if it's a HighlightAnnotation
    return annotation instanceof NutrientViewer.Annotations.HighlightAnnotation;
  }, []);

  const hasAssociatedComments = useCallback(async (annotationId) => {
    const instance = instanceRef.current;
    if (!instance) return false;

    try {
      const comments = await instance.getComments();
      return comments.some((comment) => comment.rootId === annotationId);
    } catch (err) {
      console.warn('[useNutrientViewer] Failed to check comments:', err);
      return false;
    }
  }, []);

  const deleteOrphanMarkupAnnotation = useCallback(
    async (annotationId) => {
      const instance = instanceRef.current;
      if (!instance || isUnloadedRef.current) return;

      try {
        // check it exists and has no comments
        const hasComments = await hasAssociatedComments(annotationId);
        if (hasComments) {
          console.log(
            '[useNutrientViewer] Annotation has comments, keeping:',
            annotationId
          );
          return;
        }

        // Delete the orphan annotation
        await instance.delete(annotationId);
        console.log(
          '[useNutrientViewer] Deleted orphan markup annotation:',
          annotationId
        );
      } catch (err) {
        // Annotation may have already been deleted or comment was added
        console.warn(
          '[useNutrientViewer] Could not delete orphan annotation:',
          err.message
        );
      }
    },
    [hasAssociatedComments]
  );

  const scheduleOrphanCheck = useCallback(
    (annotationId) => {
      // Clear any existing timer for this annotation
      const existingTimer =
        pendingMarkupCheckTimersRef.current.get(annotationId);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Schedule the check
      const timerId = setTimeout(() => {
        pendingMarkupCheckTimersRef.current.delete(annotationId);
        deleteOrphanMarkupAnnotation(annotationId);
      }, ORPHAN_CHECK_DELAY_MS);

      pendingMarkupCheckTimersRef.current.set(annotationId, timerId);
    },
    [deleteOrphanMarkupAnnotation]
  );

  const cancelOrphanCheck = useCallback((annotationId) => {
    const timerId = pendingMarkupCheckTimersRef.current.get(annotationId);
    if (timerId) {
      clearTimeout(timerId);
      pendingMarkupCheckTimersRef.current.delete(annotationId);
    }
  }, []);

  const clearAllOrphanCheckTimers = useCallback(() => {
    pendingMarkupCheckTimersRef.current.forEach((timerId) => {
      clearTimeout(timerId);
    });
    pendingMarkupCheckTimersRef.current.clear();
  }, []);

  const cleanup = useCallback(async () => {
    isUnloadedRef.current = true;

    clearAllOrphanCheckTimers();

    if (
      instanceRef.current &&
      NutrientRef.current &&
      viewerContainerRef.current
    ) {
      try {
        await NutrientRef.current.unload(viewerContainerRef.current);
      } catch (err) {
        console.warn('[useNutrientViewer] Unload warning:', err.message);
      }
      instanceRef.current = null;
    }

    if (viewerContainerRef.current && viewerContainerRef.current.parentNode) {
      viewerContainerRef.current.parentNode.removeChild(
        viewerContainerRef.current
      );
      viewerContainerRef.current = null;
    }

    footingAnnotationIdsRef.current.clear();
    footingDataMapRef.current.clear();
    activeFootingIdRef.current = null;

    setIsReady(false);
    setIsDocumentRestricted(false);
  }, [clearAllOrphanCheckTimers]);

  const initializeViewer = useCallback(async () => {
    const container = containerRef.current;

    if (!container || !documentId || !authToken) {
      return;
    }

    // prevent concurrent initializations
    if (isInitializingRef.current) {
      console.log('[useNutrientViewer] Already initializing, skipping...');
      return;
    }

    isInitializingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const NutrientViewer = (await import('@nutrient-sdk/viewer')).default;
      NutrientRef.current = NutrientViewer;

      await cleanup();

      if (!containerRef.current) {
        isInitializingRef.current = false;
        setIsLoading(false);
        return;
      }

      // Creating a separate container for Nutrient viewer to isolate Nutrient's DOM manipulation
      // This was messing up other React components inside the containerRef during HMR
      const viewerContainer = document.createElement('div');
      viewerContainer.style.cssText = 'width: 100%; height: 100%;';
      containerRef.current.appendChild(viewerContainer);
      viewerContainerRef.current = viewerContainer;

      // custom zoom level display element
      const zoomLevelElement = document.createElement('div');
      zoomLevelElement.style.cssText =
        'color: #67716E; font-size: 13px; font-weight: 500; min-width: 45px; text-align: center; user-select: none;';
      zoomLevelElement.textContent = '100%';
      zoomLevelElementRef.current = zoomLevelElement;

      // toolbar items
      // https://www.nutrient.io/api/web/variables/NutrientViewer.defaultToolbarItems.html
      const pager = NutrientViewer.defaultToolbarItems.find(
        (item) => item.type === 'pager'
      );
      const zoomOut = NutrientViewer.defaultToolbarItems.find(
        (item) => item.type === 'zoom-out'
      );
      const zoomIn = NutrientViewer.defaultToolbarItems.find(
        (item) => item.type === 'zoom-in'
      );
      const text = NutrientViewer.defaultToolbarItems.find(
        (item) => item.type === 'text'
      );

      const toolbarItems = [
        pager,
        zoomOut,
        {type: 'custom', node: zoomLevelElement, id: 'zoom-level-display'},
        zoomIn,
        {type: 'comment'},
        text,
      ].filter(Boolean);

      const instance = await NutrientViewer.load({
        documentId,
        serverUrl,
        container: viewerContainer,
        instant: false,
        autoSaveMode: 'IMMEDIATE',
        authPayload: {jwt: authToken},
        styleSheets: ['/pdf-review.css'],
        toolbarItems,
        useCDN: true,
        mentionableUsers,
        licenseKey: process.env.REACT_APP_NUTRIENT_LICENSE_KEY,
        inlineTextSelectionToolbarItems: (
          {defaultItems, hasDesktopLayout},
          selection
        ) => {
          const commentItem = defaultItems.find(
            (item) => item.type === 'comment'
          );
          const strikeoutItem = defaultItems.find(
            (item) => item.type === 'strikeout'
          );
          const items = [];
          if (commentItem) items.push({...commentItem, icon: CommentIcon});
          if (strikeoutItem) items.push(strikeoutItem);
          return items;
        },
      });

      instanceRef.current = instance;
      isUnloadedRef.current = false;

      // Check for document permission restrictions
      const hasRestrictions = await checkDocumentRestrictions(instance);
      setIsDocumentRestricted(hasRestrictions);
      if (hasRestrictions) {
        console.log(
          '[useNutrientViewer] Document has editing restrictions (encrypted/protected)'
        );
      }

      if (typeof creatorName === 'string' && creatorName.trim()) {
        try {
          instance.setAnnotationCreatorName(creatorName.trim());
        } catch (err) {
          console.warn(
            '[useNutrientViewer] setAnnotationCreatorName failed:',
            err
          );
        }
      }

      setIsReady(true);
      setIsLoading(false);
      isInitializingRef.current = false;
      onReady?.(instance);
    } catch (err) {
      console.error('[useNutrientViewer] Failed to initialize:', err);
      setError(err);
      setIsLoading(false);
      isInitializingRef.current = false;
      onError?.(err);
    }
  }, [containerRef, documentId, authToken, onReady, onError, cleanup]);

  useEffect(() => {
    if (!isReady || !instanceRef.current || isUnloadedRef.current) return;
    const name =
      typeof creatorName === 'string' && creatorName.trim()
        ? creatorName.trim()
        : null;
    if (name) {
      try {
        instanceRef.current.setAnnotationCreatorName(name);
      } catch (err) {
        console.warn(
          '[useNutrientViewer] setAnnotationCreatorName failed:',
          err
        );
      }
    }
  }, [isReady, creatorName]);

  // Update mentionable users when they become available after viewer init
  useEffect(() => {
    if (!isReady || !instanceRef.current || isUnloadedRef.current) return;
    if (!mentionableUsers?.length) return;
    try {
      instanceRef.current.setMentionableUsers(mentionableUsers);
    } catch (err) {
      console.warn('[useNutrientViewer] setMentionableUsers failed:', err);
    }
  }, [isReady, mentionableUsers]);

  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance || !isReady || isUnloadedRef.current) return;

    /**
     * When a new annotation is created, check if it's a markup annotation
     * that might be an orphan (created from text selection without a comment)
     */
    const handleAnnotationsCreate = (annotations) => {
      annotations.forEach((annotation) => {
        if (isTextSelectionMarkup(annotation)) {
          console.log(
            '[useNutrientViewer] Markup annotation created, scheduling orphan check:',
            annotation.id
          );
          scheduleOrphanCheck(annotation.id);
        }
      });
    };

    // When a comment is created, cancel any pending orphan check for its root annotation
    const handleCommentsCreate = (comments) => {
      comments.forEach((comment) => {
        if (comment.rootId) {
          console.log(
            '[useNutrientViewer] Comment created, canceling orphan check for:',
            comment.rootId
          );
          cancelOrphanCheck(comment.rootId);
        }
      });
    };

    instance.addEventListener('annotations.create', handleAnnotationsCreate);
    instance.addEventListener('comments.create', handleCommentsCreate);

    return () => {
      try {
        instance.removeEventListener(
          'annotations.create',
          handleAnnotationsCreate
        );
        instance.removeEventListener('comments.create', handleCommentsCreate);
      } catch (err) {
        // Instance may already be unloaded
      }
      clearAllOrphanCheckTimers();
    };
  }, [
    isReady,
    isTextSelectionMarkup,
    scheduleOrphanCheck,
    cancelOrphanCheck,
    clearAllOrphanCheckTimers,
  ]);

  // Track zoom level changes
  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance || !isReady || isUnloadedRef.current) return;

    const updateZoomLevel = (zoom) => {
      const zoomPercent = Math.round(zoom * 100);
      // Update the toolbar DOM element
      if (zoomLevelElementRef.current) {
        zoomLevelElementRef.current.textContent = `${zoomPercent}%`;
      }
    };

    // Set initial zoom level
    const initialZoom = instance.currentZoomLevel;
    if (initialZoom) {
      updateZoomLevel(initialZoom);
    }

    const handleViewStateChange = () => {
      const zoom = instanceRef.current?.currentZoomLevel;
      if (zoom) {
        updateZoomLevel(zoom);
      }
    };

    instance.addEventListener('viewState.change', handleViewStateChange);

    return () => {
      try {
        instance.removeEventListener('viewState.change', handleViewStateChange);
      } catch (err) {
        // Instance may already be unloaded
      }
    };
  }, [isReady]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  useEffect(() => {
    if (documentId && containerRef.current && authToken) {
      initializeViewer();
    }
  }, [documentId, authToken]); // do not include initializeViewer to prevent infinite loops

  const goToPage = useCallback((pageNumber) => {
    const instance = instanceRef.current;

    if (!instance || isUnloadedRef.current) {
      return;
    }

    try {
      instance.setViewState((viewState) =>
        viewState.set('currentPageIndex', pageNumber)
      );
    } catch (err) {
      console.error('[useNutrientViewer] Failed to navigate to page:', err);
    }
  }, []);

  const getCurrentPageIndex = useCallback(() => {
    const instance = instanceRef.current;
    if (!instance) {
      return 0;
    }
    return instance.viewState.currentPageIndex;
  }, []);

  const getTotalPages = useCallback(() => {
    const instance = instanceRef.current;
    if (!instance) {
      return 0;
    }
    return instance.totalPageCount;
  }, []);

  return {
    // state
    isReady,
    isLoading,
    error,
    authToken,
    isDocumentRestricted,
    // refs
    instance: instanceRef.current,
    NutrientViewer: NutrientRef.current,
    // methods
    initializeViewer,
    goToPage,
    getCurrentPageIndex,
    getTotalPages,
  };
};

export default useNutrientViewer;
